import buildStorageApi from 'app-components/flux/storage/api';
import { RECEIPT_STORAGE_DOMAIN } from 'app-components/flux/storage/receiptStorageSlice';
import type { DocumentType } from 'app-components/flux/storage/types';

import { isLocalReceipt } from './receiptsPredicates';
import type { Receipt, ReceiptSubStatus } from './receiptsTypes';

export const RECEIPT_EXPORT_ACTION_LABEL = 'Download receipt';
export const RECEIPT_EXPORT_PREPARING_LABEL = 'Preparing receipt…';
export const RECEIPT_EXPORT_FAILED_MESSAGE = "We couldn't download this receipt. Please try again.";
export const RECEIPT_EXPORT_UNAVAILABLE_MESSAGE = 'The original file for this receipt is no longer available.';

const DEFAULT_EXPORT_MIME_TYPE = 'application/octet-stream';
const MAX_FILE_NAME_LENGTH = 120;

/** Canonical extension per stored mime type; the alias lists accept equivalent stored extensions. */
const EXTENSIONS_BY_MIME_TYPE: Readonly<Record<string, { extension: string; aliases: readonly string[] }>> = {
  'application/pdf': { extension: 'pdf', aliases: ['pdf'] },
  'image/heic': { extension: 'heic', aliases: ['heic'] },
  'image/heif': { extension: 'heif', aliases: ['heif'] },
  'image/jpeg': { extension: 'jpg', aliases: ['jpg', 'jpeg'] },
  'image/jpg': { extension: 'jpg', aliases: ['jpg', 'jpeg'] },
  'image/png': { extension: 'png', aliases: ['png'] },
};

/** The original upload never landed in storage, or storage blocked it. */
const UNAVAILABLE_RECEIPT_SUB_STATUSES: ReadonlySet<ReceiptSubStatus> = new Set([
  'UPLOAD_FAILED',
  'UPLOAD_EXPIRED',
  'MALWARE_DETECTED',
  'QUOTA_EXCEEDED',
  'STORAGE_NOT_READY',
  'CREATION_FAILED',
]);

const UNAVAILABLE_DOCUMENT_SUB_STATUSES: ReadonlySet<NonNullable<DocumentType['subStatus']>> = new Set([
  'UPLOAD_PENDING',
  'UPLOAD_PROCESSING',
  'UPLOAD_FAILED',
  'UPLOAD_EXPIRED',
  'FILE_LIMIT_EXCEEDED',
  'FILE_SIZE_EXCEEDED',
  'QUOTA_EXCEEDED',
  'MALWARE_SCAN_FAILED',
]);

/** Thrown when the original is incomplete, blocked, or removed; retrying cannot help. */
export class ReceiptExportUnavailableError extends Error {
  constructor(message = 'Receipt original file is unavailable for export.') {
    super(message);
    this.name = 'ReceiptExportUnavailableError';
  }
}

export type ReceiptExportFile = {
  /** Freshly signed URL for the original (PRIMARY) file, never the preview thumbnail. */
  url: string;
  fileName: string;
  mimeType: string;
};

export type ReceiptExportFields = Pick<Receipt, 'id' | 'documentId' | 'status' | 'subStatus' | 'isDeleted'> &
  Partial<Pick<Receipt, 'name' | 'mimeType'>>;

/**
 * Whether details may offer the original file. Extraction failures and unlinked receipts still
 * have their original; local placeholders and blocked uploads do not.
 */
export const canExportReceipt = (receipt: ReceiptExportFields): boolean =>
  !isLocalReceipt(receipt) &&
  !receipt.isDeleted &&
  receipt.status !== 'UPLOAD_PENDING' &&
  !(receipt.subStatus && UNAVAILABLE_RECEIPT_SUB_STATUSES.has(receipt.subStatus));

// Control characters, path separators, and characters Windows or macOS reject in file names.
// biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what this strips.
const UNSAFE_FILE_NAME_CHARACTERS = /[\u0000-\u001f\u007f<>:"/\\|?*]+/g;
const RESERVED_WINDOWS_FILE_NAME = /^(con|prn|aux|nul|com\d|lpt\d)$/i;

const sanitizeFileName = (name: string): string => {
  const baseName = name.split(/[\\/]/).pop() ?? '';

  return baseName
    .replace(UNSAFE_FILE_NAME_CHARACTERS, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+|\.+$/g, '')
    .trim();
};

const splitExtension = (fileName: string): { stem: string; extension?: string } => {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0 || dotIndex === fileName.length - 1) {
    return { stem: fileName };
  }

  return { stem: fileName.slice(0, dotIndex), extension: fileName.slice(dotIndex + 1).toLowerCase() };
};

/**
 * Keeps the stored file name when it is safe, and guarantees the extension matches the stored mime
 * type. Missing or unsafe names fall back to `receipt-<receiptId>.<ext>`, so repeat exports match.
 */
export const getReceiptExportFileName = ({
  name,
  mimeType,
  receiptId,
}: {
  name?: string | null;
  mimeType?: string | null;
  receiptId: string;
}): string => {
  const knownType = mimeType ? EXTENSIONS_BY_MIME_TYPE[mimeType.toLowerCase()] : undefined;
  const sanitized = sanitizeFileName(name ?? '');
  const { stem, extension } = splitExtension(sanitized);
  const safeStem = stem.slice(0, MAX_FILE_NAME_LENGTH).trim();
  const fallbackStem = `receipt-${receiptId.replace(UNSAFE_FILE_NAME_CHARACTERS, '')}`;
  const isUsableStem = safeStem.length > 0 && !RESERVED_WINDOWS_FILE_NAME.test(safeStem);
  const resolvedStem = isUsableStem ? safeStem : fallbackStem;

  if (knownType) {
    const resolvedExtension = extension && knownType.aliases.includes(extension) ? extension : knownType.extension;
    return `${resolvedStem}.${resolvedExtension}`;
  }

  // An unrecognized mime type cannot name an extension, so trust the stored one when present.
  return extension && isUsableStem ? `${resolvedStem}.${extension}` : resolvedStem;
};

// Only the PRIMARY artifact is the original; THUMBNAIL artifacts serve PDF previews.
const getCompletePrimaryArtifact = (
  document: DocumentType,
): NonNullable<DocumentType['artifacts']>[number] | undefined => {
  if (document.status !== 'COMPLETE') {
    return undefined;
  }
  if (document.subStatus && UNAVAILABLE_DOCUMENT_SUB_STATUSES.has(document.subStatus)) {
    return undefined;
  }

  return document.artifacts?.find((artifact) => artifact.type === 'PRIMARY' && artifact.status === 'COMPLETE');
};

const receiptStorageApi = buildStorageApi(RECEIPT_STORAGE_DOMAIN);

const resolveReceiptExportFileUncached = async (receipt: ReceiptExportFields): Promise<ReceiptExportFile> => {
  if (!canExportReceipt(receipt)) {
    throw new ReceiptExportUnavailableError();
  }

  // Call storage directly, not through the slice, so export URLs stay local and preview state is
  // untouched. The service enforces document access, and every call returns freshly signed URLs.
  const document = await receiptStorageApi.fetchById(receipt.documentId);
  if (!getCompletePrimaryArtifact(document)) {
    throw new ReceiptExportUnavailableError();
  }

  // Artifact URLs can expire; request a new URL for the original on every export and retry.
  const url = await receiptStorageApi.getCdnUrl(receipt.documentId, false);
  const mimeType = document.mimeType || receipt.mimeType || DEFAULT_EXPORT_MIME_TYPE;

  return {
    url,
    mimeType,
    fileName: getReceiptExportFileName({ name: document.name || receipt.name, mimeType, receiptId: receipt.id }),
  };
};

// Callers report and branch on real Errors, whatever shape the HTTP client rejected with.
const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  const message =
    typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string'
      ? error.message
      : 'Receipt storage request failed.';
  return new Error(message);
};

const pendingReceiptExports = new Map<string, Promise<ReceiptExportFile>>();

/**
 * Resolves a fresh signed URL plus file metadata for the receipt's original. Concurrent calls for
 * the same document share one request; call again after a failure to get a newly signed URL.
 */
export const resolveReceiptExportFile = (receipt: ReceiptExportFields): Promise<ReceiptExportFile> => {
  const pending = pendingReceiptExports.get(receipt.documentId);
  if (pending) {
    return pending;
  }

  const request = resolveReceiptExportFileUncached(receipt)
    .catch((error: unknown) => {
      throw toError(error);
    })
    .finally(() => {
      pendingReceiptExports.delete(receipt.documentId);
    });
  pendingReceiptExports.set(receipt.documentId, request);

  return request;
};
